import { useMoodTracking } from "@/lib/useMoodTracking";
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "./_shared";
import { Heart } from "lucide-react";

const MOOD_COLORS = {
  good: "bg-green-500",
  average: "bg-yellow-400",
  rough: "bg-red-500",
};
const MOOD_LABEL = { good: "Good", average: "Average", rough: "Rough" };

export default function MoodHeatmap() {
  const mood = useMoodTracking();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!mood.enabled) { setData(null); return; }
    setLoading(true);
    api.get("/moods/heatmap", { params: { days } })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [days, mood.enabled]);

  // Build the date column header (oldest → newest)
  const dateCols = useMemo(() => {
    if (!data?.from || !data?.to) return [];
    const out = [];
    let cur = new Date(data.from + "T00:00:00");
    const end = new Date(data.to + "T00:00:00");
    while (cur <= end) {
      out.push(cur.toISOString().slice(0, 10));
      cur = new Date(cur.getTime() + 86400000);
    }
    return out;
  }, [data]);

  if (!mood.enabled) return <div className="p-6 text-sm text-muted-foreground">{mood.loading ? "Loading team settings…" : mood.error ? "Could not load team settings. Please reload." : "Mood tracking is turned off for this company."}</div>;

  return (
    <div className="px-4 py-4 md:p-6 lg:p-10 max-w-[1200px] mx-auto" data-testid="mood-heatmap-page">
      <PageHeader
        tag="/ team pulse"
        title="Mood heat map"
        subtitle="Optional end-of-day check-ins. Use this to spot a crew that's grinding, not to score them."
        action={
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="rounded-sm border border-zinc-400 px-3 py-2 text-sm font-bold bg-white"
            data-testid="mood-days-select">
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        }
      />

      {/* Legend */}
      <div className="hard-card p-4 mb-6 flex flex-wrap items-center gap-4 text-xs">
        <span className="font-bold uppercase tracking-tight">Legend:</span>
        {Object.entries(MOOD_COLORS).map(([k, c]) => (
          <div key={k} className="flex items-center gap-1.5">
            <span className={`w-4 h-4 ${c} border border-zinc-300`} />
            <span>{MOOD_LABEL[k]}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5">
          <span className="w-4 h-4 bg-zinc-100 border border-zinc-200" />
          <span className="text-zinc-500">No submission</span>
        </div>
      </div>

      {loading && <div className="hard-card p-10 text-center text-sm text-zinc-500">Loading…</div>}

      {!loading && data?.items?.length === 0 && (
        <div className="hard-card p-10 text-center">
          <Heart className="w-10 h-10 mx-auto text-zinc-300 mb-3" />
          <p className="text-sm text-zinc-500">No mood check-ins yet. The crew gets prompted after each timesheet submission — give it a few days.</p>
        </div>
      )}

      {!loading && data?.items?.length > 0 && (
        <div className="hard-card overflow-x-auto">
          <table className="w-full text-xs" data-testid="mood-heatmap-table">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="text-left px-3 py-2 font-mono uppercase tracking-widest text-[10px] text-zinc-500 sticky left-0 bg-zinc-50 z-10">Worker</th>
                <th className="text-center px-2 py-2 font-mono uppercase tracking-widest text-[10px] text-zinc-500">🟢</th>
                <th className="text-center px-2 py-2 font-mono uppercase tracking-widest text-[10px] text-zinc-500">🟡</th>
                <th className="text-center px-2 py-2 font-mono uppercase tracking-widest text-[10px] text-zinc-500">🔴</th>
                {dateCols.map((d) => (
                  <th key={d} className="text-center px-0.5 py-2 font-mono text-[8px] text-zinc-400" title={d}>
                    {new Date(d + "T00:00:00").getDate()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.items.map((u) => (
                <tr key={u.user_id} className="border-b border-zinc-100" data-testid={`mood-row-${u.user_id}`}>
                  <td className="px-3 py-1.5 font-bold whitespace-nowrap sticky left-0 bg-white z-10">{u.user_name}</td>
                  <td className="text-center px-2 py-1.5 font-mono text-zinc-700">{u.counts.good}</td>
                  <td className="text-center px-2 py-1.5 font-mono text-zinc-700">{u.counts.average}</td>
                  <td className={`text-center px-2 py-1.5 font-mono ${u.counts.rough > 2 ? "text-red-600 font-bold" : "text-zinc-700"}`}>{u.counts.rough}</td>
                  {dateCols.map((d) => {
                    const m = u.days?.[d];
                    return (
                      <td key={d} className="text-center px-0 py-1.5">
                        <span
                          className={`inline-block w-4 h-4 border border-zinc-200 ${m ? MOOD_COLORS[m] : "bg-zinc-100"}`}
                          title={m ? `${d}: ${MOOD_LABEL[m]}` : `${d}: no check-in`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
